/* eslint-disable max-len */
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReferenceBrandType } from '@1po/1po-bff-fe-spec/generated/catalog/easy_basket/response/GetEasyBasketOperationDetailResponse';
import { RootState } from 'app/AppStore';
import { MapIcon, PlusOutlinedIcon } from 'assets/icons';
import CopyReferenceButton from 'components/CopyReferenceButton';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import ProductModal from 'components/ProductModal';
import StockDisplay from 'components/StockInfo';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import {
  addReferenceSelection,
  getEasyBasketReference,
  getSelectedReference,
  removeReferenceSelection,
} from 'domains/easyBasket/EasyBasket.store';
import { OperationItemPrice } from 'domains/easyBasket/EasyBasket.types';
import { getPrice } from 'domains/references';
import { useFetchPrices } from 'domains/references/References.requests';
import { SupersessionTooltip } from 'pages/CatalogPage/common/ReferenceSection/SupersessionTooltip';
import {
  AlternativeCrosslink,
  ConnectionLink,
  Crosslink,
  CrosslinkCardWrapper,
  IconWrapper,
  NarrowLink,
  EasyBasketCardWrapper,
  SelectedQuantity,
} from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection/PartsSelection.styled';
import { PriceDisplay } from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection/PriceDisplay';
import { ReferenceProductModal } from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/PartsSelection/ReferenceProductModal';
import { LAST, MIDDLE, PartPosition } from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/utils';
import { getReferenceLogo } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ReferenceLogo';
import { Box, CenteredSpin, Empty, Flex, Icon, LinkRoundButton, MarginBox, Text } from 'UI';
import { getData, isLoaded } from 'utils';
import PlateModal from './PlateModal/PlateModal';

interface EasyBasketReferenceCardProps {
  mainReferenceNumber: string;
  referenceNumber: string;
  designation: string;
  brand: ReferenceBrandType;
  itemGroup: string;
  isMultipleReferencesGroup?: boolean;
  quantity?: string;
  isAlternative?: boolean;
  mainReferencePosition?: PartPosition;
  alternativeReferencePosition?: PartPosition;
  hasSuperSessionChain?: boolean;
  isOnlyOriginalPartReference: boolean;
}

const EasyBasketReferenceCard = ({
  itemGroup,
  isMultipleReferencesGroup,
  mainReferenceNumber,
  referenceNumber,
  designation,
  brand,
  quantity,
  isAlternative,
  mainReferencePosition,
  alternativeReferencePosition,
  hasSuperSessionChain = false,
  isOnlyOriginalPartReference,
}: EasyBasketReferenceCardProps) => {
  const dispatch = useDispatch();
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const price = useSelector((state: RootState) => getPrice(state, referenceNumber));
  const isPriceLoaded = isLoaded(price);
  const priceData = getData(price);
  const selectedReference = useSelector((state: RootState) => getSelectedReference(state, itemGroup));
  const isSelected =
    selectedReference?.mainReferenceNumber === mainReferenceNumber &&
    selectedReference?.referenceNumber === referenceNumber;
  const easyBasketReference = useSelector((state: RootState) =>
    getEasyBasketReference(state, { itemGroup, mainReferenceNumber, referenceNumber }),
  );
  const plateLines = easyBasketReference?.plateLines;

  useFetchPrices([referenceNumber], vehicleDetail?.vehicleKey);

  const selectReference = useCallback(() => {
    if (isPriceLoaded) {
      dispatch(
        addReferenceSelection({
          itemGroup,
          reference: {
            mainReferenceNumber,
            referenceNumber,
            quantity: Number(quantity ?? 1),
            price: priceData
              ? ({
                  currency: priceData.currency,
                  priceVatExcluded: priceData.garageView?.vatExcludedPrice,
                  priceVatIncluded: priceData.garageView?.vatIncludedPrice,
                } as OperationItemPrice)
              : undefined,
          },
        }),
      );
    }
  }, [dispatch, isPriceLoaded, itemGroup, mainReferenceNumber, priceData, quantity, referenceNumber]);

  useEffect(() => {
    if ((!isMultipleReferencesGroup && !isAlternative) || (isOnlyOriginalPartReference && !isAlternative)) {
      selectReference();
    }
  }, [isAlternative, isMultipleReferencesGroup, isOnlyOriginalPartReference, priceData, selectReference]);

  const getQuantity = () => {
    if (quantity == null || quantity === '') {
      return undefined;
    }
    const n = Number(quantity);
    return Number.isNaN(n) ? undefined : n;
  };

  const renderReferenceCard = () => (
    <EasyBasketCardWrapper
      padding={'0 15px 0 15px'}
      align={'center'}
      minHeight={92}
      gap={10}
      justify={'space-between'}
      isAlternative={isAlternative}
      multipleReferencesGroup={isMultipleReferencesGroup}
    >
      <Flex gap={7} align={'center'} maxWidth={150} justify={'center'}>
        <ReferenceProductModal referenceNumber={referenceNumber} />
        <MarginBox mt={5}>
          <CopyReferenceButton referenceNumber={referenceNumber} />
        </MarginBox>
      </Flex>
      {!isAlternative && <Box width={80} />}
      <Flex gap={5} maxWidth={230}>
        <ProductModal
          triggerComponent={
            <Text type="base" disableGutter hoverUnderLine cursor={'pointer'} data-cy={'link-reference-number'}>
              {designation}
            </Text>
          }
          referenceNumber={referenceNumber}
          isVehicleCatalog
        />
      </Flex>
      <Flex maxWidth={70}>{getReferenceLogo(brand)}</Flex>
      <Flex maxWidth={50} justify={'center'}>
        {quantity ?? ''}
      </Flex>
      <Flex gap={15} justify={'center'}>
        {hasSuperSessionChain && (
          <ProductModal
            triggerComponent={
              <IconWrapper>
                <SupersessionTooltip>
                  <LinkRoundButton bgColor={undefined} hoverFill={undefined} size={16} />
                </SupersessionTooltip>
              </IconWrapper>
            }
            referenceNumber={referenceNumber}
            scrollToSubstitutes
          />
        )}
        <Box width={40}>
          {plateLines && plateLines.length > 0 && (
            <PlateModal
              triggerComponent={
                <IconWrapper>
                  <Icon IconComponent={MapIcon} size={24} />
                </IconWrapper>
              }
              plateLines={plateLines}
            />
          )}
        </Box>
        <Box width={40}>
          <IconWrapper>
            <StockDisplay
              vehicleKey={vehicleDetail?.vehicleKey}
              referenceNumber={referenceNumber}
              isApplicableToCurrentVehicle
              small
              centered
              spinSize={'default'}
              fetchQuantity={getQuantity()}
            />
          </IconWrapper>
        </Box>
      </Flex>
      <DataContainer
        data={price}
        ErrorState={() => (
          <Flex maxWidth={246}>
            <ErrorWithLabel narrow />
          </Flex>
        )}
        NotFound={() => (
          <Flex maxWidth={246}>
            <Empty />
          </Flex>
        )}
        Loading={() => (
          <Flex maxWidth={246}>
            <CenteredSpin />
          </Flex>
        )}
      >
        <Flex direction={'column'} maxWidth={200} size={0}>
          <PriceDisplay priceData={priceData} />
        </Flex>
        <Flex maxWidth={36}>
          {isSelected ? (
            <SelectedQuantity onClick={() => dispatch(removeReferenceSelection({ itemGroup }))}>
              <Text type="lg" bold color="white" cursor={'pointer'}>
                {selectedReference.quantity}
              </Text>
            </SelectedQuantity>
          ) : (
            <Icon IconComponent={PlusOutlinedIcon} onClick={selectReference} />
          )}
        </Flex>
      </DataContainer>
    </EasyBasketCardWrapper>
  );

  const renderMainReferenceCrosslink = (position?: PartPosition) => {
    if (isMultipleReferencesGroup) {
      if (isAlternative && position !== LAST) {
        return (
          <Box width={30}>
            <Crosslink position={MIDDLE} />
          </Box>
        );
      }
      if (!isAlternative && position === MIDDLE) {
        return (
          <Box width={30}>
            <NarrowLink />
            <ConnectionLink />
          </Box>
        );
      }
      if (!isAlternative) {
        return (
          <Box width={30}>
            <Crosslink position={position} />
          </Box>
        );
      }
    }
    return <Box width={30} />;
  };

  const renderAlternativeReferenceCrosslink = (position?: PartPosition) => {
    if (!isAlternative) {
      return null;
    }

    return (
      <Box width={80}>
        <MarginBox ml={50}>
          <AlternativeCrosslink position={position} />
        </MarginBox>
      </Box>
    );
  };

  return (
    <CrosslinkCardWrapper direction={'row'}>
      {renderMainReferenceCrosslink(mainReferencePosition)}
      {renderAlternativeReferenceCrosslink(alternativeReferencePosition)}
      {renderReferenceCard()}
    </CrosslinkCardWrapper>
  );
};

export default EasyBasketReferenceCard;
